import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

/**
 * Floating cargo containers. Performant — uses InstancedMesh with per-frame
 * positional updates limited to N=18 instances.
 */
const COUNT = 18;
const COLORS = [
    "#D69331",
    "#1a1a1a",
    "#2a2a2a",
    "#4a4a4a",
    "#0f0f0f",
    "#D69331",
];

export function Containers({ scrollRef }) {
    const meshRef = useRef();
    const dummy = useMemo(() => new THREE.Object3D(), []);

    const seeds = useMemo(
        () =>
            Array.from({ length: COUNT }).map((_, i) => ({
                radius: 3.2 + Math.random() * 2.8,
                phi: Math.random() * Math.PI * 2,
                theta: (Math.random() - 0.5) * Math.PI * 0.6,
                speed: 0.05 + Math.random() * 0.12,
                tilt: Math.random() * Math.PI,
                color: COLORS[i % COLORS.length],
                size: 0.16 + Math.random() * 0.14,
            })),
        [],
    );

    // Colors per-instance
    const colorArr = useMemo(() => {
        const arr = new Float32Array(COUNT * 3);
        seeds.forEach((s, i) => {
            const c = new THREE.Color(s.color);
            arr[i * 3] = c.r;
            arr[i * 3 + 1] = c.g;
            arr[i * 3 + 2] = c.b;
        });
        return arr;
    }, [seeds]);

    useFrame((state) => {
        if (!meshRef.current) return;
        const t = state.clock.elapsedTime;
        const scroll = scrollRef?.current ?? 0;
        seeds.forEach((s, i) => {
            const phi = s.phi + t * s.speed;
            const theta = s.theta + Math.sin(t * 0.3 + i) * 0.12;
            const r = s.radius + Math.sin(t * 0.5 + i) * 0.15;
            // Drift containers further out as user scrolls
            const expand = 1 + scroll * 0.8;
            const x = Math.cos(phi) * Math.cos(theta) * r * expand;
            const y = Math.sin(theta) * r * 0.6;
            const z = Math.sin(phi) * Math.cos(theta) * r * expand;
            dummy.position.set(x, y, z);
            dummy.rotation.set(
                s.tilt + t * s.speed * 0.5,
                phi,
                Math.sin(t * 0.4 + i) * 0.2,
            );
            const sz = s.size;
            dummy.scale.set(sz * 2.4, sz, sz);
            dummy.updateMatrix();
            meshRef.current.setMatrixAt(i, dummy.matrix);
        });
        meshRef.current.instanceMatrix.needsUpdate = true;
    });

    return (
        <instancedMesh
            ref={meshRef}
            args={[null, null, COUNT]}
            castShadow={false}
            receiveShadow={false}
        >
            <boxGeometry args={[1, 1, 1]}>
                <instancedBufferAttribute
                    attach="attributes-color"
                    args={[colorArr, 3]}
                />
            </boxGeometry>
            <meshStandardMaterial
                vertexColors
                roughness={0.55}
                metalness={0.35}
            />
        </instancedMesh>
    );
}
